import { useCallback, useEffect, useMemo, useState } from "react";
import { bool, string, func } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { getProjectDetailsQuery, getTeamMembersQuery } from "@query";
import { parseWorkTypesOrMetadata, universalDateParser } from "@utils";
import { useActiveProjectStore, useCheckProjectAccess, useStores } from "@hooks";
import { Dialog, InfoLabel, ClearButton, Alert } from "@components";
import { ProjectAccessDeniedDialog } from "@dialogs";
import { Grid, Hidden } from "@material-ui/core";
import { DescriptionEditor, ProjectChat, Header, Footer, ActionPanel } from "./components";
import { Comment } from "@assets";
import useStyle from "./ProjectEditionDialog.style";

export const ProjectEditionDialog = observer(({
  isOpen,
  onClose,
  projectUuid,
  noDialog
}) => {

  const { userStore } = useStores();
  const activeProjects = useActiveProjectStore();
  const { t } = useTranslation();
  const classes = useStyle(isMobile);

  const [ showCommentInput, setShowCommentInput ] = useState(false);
  const [ teamMembers, setTeamMembers ] = useState([]);
  const [ accessDenied, setAccessDenied ] = useState(null);
  const [ projectNotFound, setProjectNotFound ] = useState(false);

  const {
    id,
    uuid,
    createdAt,
    dueOn,
    name,
    tags,
    metadata,
    users,
    content,
    attachments,
    type,
    status,
    btSetUp,
    btExported,
    hasComments,
  } = activeProjects.currentProject;

  const { isOwner, isObserver, isContractor } = useCheckProjectAccess(users || {});

  const deadline = useMemo(() => (
    dueOn ? universalDateParser(dueOn) : undefined
  ), [dueOn]);

  const created = useMemo(() => (
    createdAt ? universalDateParser(createdAt) : undefined
  ), [createdAt]);

  const isMini = useMemo(() => type === "mini", [type]);

  const pdfProposals = useMemo(() => attachments ? attachments.filter((x) => x.type === "proposal") : [], [attachments]);
  const fileAttachments = useMemo(() => attachments ? attachments.filter((x) => x.type !== "proposal") : [], [attachments]);

  const chatUsers = useMemo(() => [
    ...(users.observer || []).filter(Boolean),
    users.author,
    ...(users.owner && users.owner.id !== users.author?.id ? [users.owner] : []),
  ].filter((u) => u && u.id !== userStore.data.id), [users, userStore.data.id]);

  const workTypes = useMemo(() => (
    parseWorkTypesOrMetadata(activeProjects.workTypes, tags, metadata)
  ), [metadata, tags, activeProjects.workTypes]);

  const loadData = async (isMounted = true) => {
    try {
      const details = await getProjectDetailsQuery(projectUuid);
      const u = await getTeamMembersQuery(userStore.workspaceUuid);
      if (!isMounted) return;
      setTeamMembers(u);
      activeProjects.updateLocalProject(projectUuid, details);
    } catch (error) {
      if (!isMounted) return;
      if (error.code === "PROJECT_ACCESS_DENIED") {
        setAccessDenied({
          projectUuid: error.projectUuid,
          projectName: error.projectName,
          ownerEmail: error.ownerEmail,
        });
      } else if (error.code === "PROJECT_NOT_FOUND") {
        setProjectNotFound(true);
      } else {
        console.error("[ProjectEditionDialog] Unexpected error loading project:", error);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      await loadData(isMounted);
    };
    load();
    return () => { isMounted = false; };
  }, []);

  const handleAccessDeniedClose = useCallback(() => {
    setAccessDenied(null);
    onClose?.(false);
  }, [ onClose ]);

  const handleProjectNotFoundClose = useCallback(() => {
    setProjectNotFound(false);
    onClose?.(false);
  }, [ onClose ]);

  const openCommentInput = useCallback(() => setShowCommentInput(true), []);
  const closeCommentInput = useCallback(() => setShowCommentInput(false), []);

  const actionPanel = (
    <ActionPanel
      uuid={uuid}
      isOwner={isOwner}
      isContractor={isContractor}
      isMini={isMini}
      attachments={isMini ? pdfProposals : fileAttachments}
      author={users.author}
      owner={users.owner}
      users={users.observer}
      teamMembers={teamMembers}
      workTypes={workTypes}
      contractors={users.contractor || users.contractors || []}
      sticky={false}
    />
  );

  const actionPanelSticky = (
    <ActionPanel
      uuid={uuid}
      isOwner={isOwner}
      isContractor={isContractor}
      isMini={isMini}
      attachments={isMini ? pdfProposals : fileAttachments}
      author={users.author}
      owner={users.owner}
      users={users.observer}
      teamMembers={teamMembers}
      workTypes={workTypes}
      contractors={users.contractor || users.contractors || []}
      sticky={true}
    />
  );

  const editorContent = useMemo(() => (
    <Grid
      item
      container
      direction="column"
      wrap="nowrap"
      className={classes.scrollbarContainer}
    >
      <InfoLabel label="views.active.popup.desc" />
      <DescriptionEditor
        description={content || ""}
        uuid={projectUuid}
        allowEdition={isOwner && !isContractor}
      />
      {noDialog ? (
        actionPanel
      ) : (
        <Hidden smUp>{actionPanel}</Hidden>
      )}

      <InfoLabel
        label="views.active.popup.activity"
        action={
          isObserver ? (
            <ClearButton
              icon={<Comment />}
              onClick={openCommentInput}
            >
              {t("views.active.popup.add_comment")}
            </ClearButton>
          ) : undefined
        }
      />
      <ProjectChat
        projectUuid={uuid}
        noAdding={!isObserver}
        users={chatUsers}
        teamMembers={teamMembers}
        showCommentInput={showCommentInput}
        onCommentCommit={closeCommentInput}
      />
    </Grid>
  ), [content, projectUuid, isOwner, isContractor, noDialog, actionPanel, isObserver, openCommentInput, chatUsers, teamMembers, uuid, showCommentInput, closeCommentInput, t]);

  const accessDeniedPortal = accessDenied && (
    <ProjectAccessDeniedDialog
      open={true}
      onClose={handleAccessDeniedClose}
      projectUuid={accessDenied.projectUuid}
      projectName={accessDenied.projectName}
      ownerEmail={accessDenied.ownerEmail}
    />
  );

  const projectNotFoundAlert = projectNotFound && (
    <Alert
      isOpen
      title={t("dialogs.projectNotFound.title")}
      onAccept={handleProjectNotFoundClose}
      acceptText={t("common.close")}
    >
      {t("dialogs.projectNotFound.message")}
    </Alert>
  );

  return noDialog ? (
    <>
      <Grid item container className={classes.noDialogHeader}>
        <Header
          uuid={uuid}
          name={name}
          description={content}
          isOwner={isOwner}
          isContractor={isContractor}
          isMini={isMini}
          isBusiness={userStore.isBusinessUser}
          deadline={deadline}
          created={created}
          status={status}
          id={id}
          projectBtSetUp={btSetUp}
          projectBtExported={btExported}
          hasComments={hasComments}
          showActionMenu
        />
      </Grid>
      {editorContent}
      {accessDeniedPortal}
      {projectNotFoundAlert}
    </>
  ) : (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        width={850}
        disableEnforceFocus
        fullActionPaddings
        title={
          <Header
            uuid={uuid}
            name={name}
            description={content}
            isOwner={isOwner}
            isContractor={isContractor}
            isMini={isMini}
            isBusiness={userStore.isBusinessUser}
            deadline={deadline}
            created={created}
            status={status}
            id={id}
            projectBtSetUp={btSetUp}
            projectBtExported={btExported}
            hasComments={hasComments}
          />
        }
        actions={<Footer
          uuid={uuid}
          isOwner={isOwner}
          isContractor={isContractor}
          isMini={isMini}
          isBusiness={userStore.isBusinessUser}
          name={name}
          description={content}
          id={id}
          projectBtSetUp={btSetUp}
          projectBtExported={btExported}
          hasComments={hasComments}
        />}
      >
        <Hidden xsDown>
          <Grid item container spacing={6}>
            <Grid item container xs={6} md={8}>
              {editorContent}
            </Grid>
            <Grid item container xs={6} md={4} direction="column">
              {actionPanelSticky}
            </Grid>
          </Grid>
        </Hidden>
        <Hidden smUp>{editorContent}</Hidden>
      </Dialog>
      {accessDeniedPortal}
      {projectNotFoundAlert}
    </>
  );
});

ProjectEditionDialog.propTypes = {
  noDialog: bool,
  isOpen: bool,
  onClose: func,
  projectUuid: string.isRequired,
};
